/**
 * TetherSkillEffect — Conjuration Tether path active: project a mirrored shade.
 *
 * Toggle skill that spawns a shade of the player 3 tiles behind them (opposite
 * facing direction). The shade mirrors the player's movement in reverse — when
 * the player moves right, the shade moves left. It deals 40% of the player's
 * base damage and shares the player's HP pool: any damage the shade takes is
 * deducted from the player's health directly. The shade phases through walls
 * (a projection, not a physical entity).
 *
 * Toggle on → shade appears at mirrored position. Toggle off → shade dismissed,
 * cooldown starts. The shade also vanishes if the player dies (integration layer
 * calls `onShadeDestroyed()`).
 *
 * Upgrade hooks (resolved here, values come from UpgradeEffectResolver):
 * - Clarity (tether_clarity): shadeDamageMultiplier (max), mirrorDistanceTiles (callback)
 * - Synapse (tether_synapse): twinAttackStaggerMs, twinAttackWindowMs
 * - Recursive (tether_path_passive): shadeCritChance, shadeCritStaggerMs
 *
 * The shade's AI behavior (mirror movement, attack resolution, HP routing) is
 * handled by the integration layer via the 'mirror' AI mode. This skill is
 * responsible only for the spawn/dismiss lifecycle and resolver key resolution.
 *
 * Medium cooldown tier (1200ms). Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Tether
 */

import { ToggleSkillEffect } from '../../ToggleSkillEffect.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Config object passed to SummonSystem.spawn() for shade summons. */
export interface ShadeSummonConfig {
  readonly id: string;
  readonly archetype: 'shade';
  readonly baseHp: number;
  readonly baseDamage: number;
  readonly baseSpeed: number;
  readonly leashRadiusTiles: number;
  readonly aggroWeight: number;
  /**
   * AI movement mode. Consumed by the integration layer;
   * SummonSystem itself is movement-mode agnostic.
   * - 'mirror': position = player position + (facing × -1 × mirrorDistanceTiles × tileSize)
   */
  readonly aiMode: 'mirror' | 'autonomous' | 'autonomous-detached';
  /** Fraction of player's base damage the shade deals (0–1). */
  readonly shadeDamageMultiplier: number;
  /** Distance from the player in tiles, in the opposite direction of travel. */
  readonly mirrorDistanceTiles: number;
  /** When true, shade damage routes to the player's HP pool. No independent death. */
  readonly sharedHpPool: boolean;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface TetherDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  readonly player: {
    getPosition(): { x: number; y: number };
    getFacingDirection(): { x: number; y: number };
    getBaseDamage(): number;
  };
  readonly targeting: {
    /** Returns true if the shade should fail to manifest (XP-based freshness check). */
    shouldFizzle(): boolean;
  };
  readonly summons: {
    spawn(config: ShadeSummonConfig, x: number, y: number): string;
    destroy(instanceId: string): void;
  };
  /**
   * Optional override for mirror distance in tiles. Injected by Clarity upgrade
   * (ClaritySkillEffect.getCurrentMirrorDistanceTiles). When absent, falls back
   * to config.mirrorDistanceTiles (3).
   */
  readonly getMirrorDistanceTiles?: () => number;
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface TetherSkillConfig {
  readonly cooldownMs: number;
  /** Fraction of player's base damage the shade deals (0–1). */
  readonly shadeDamageMultiplier: number;
  /** Mirror distance in tiles. */
  readonly mirrorDistanceTiles: number;
  /** Shade movement speed (integration layer applies px/s). */
  readonly shadeSpeed: number;
  /** How much enemy aggro the shade draws (0–1). */
  readonly shadeAggroWeight: number;
  /** Tile size in pixels. Matches TILE_SIZE from Constants.ts. */
  readonly tileSize: number;
}

const DEFAULT_CONFIG: TetherSkillConfig = {
  cooldownMs: 1200,            // medium cooldown tier
  shadeDamageMultiplier: 0.4,  // spec: 40% of player's base damage
  mirrorDistanceTiles: 3,      // spec: 3 tiles opposite facing
  shadeSpeed: 1.0,             // integration layer applies px/s
  shadeAggroWeight: 0.0,       // shade is a projection, draws no aggro
  tileSize: 32,                // TILE_SIZE constant from Constants.ts
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class TetherSkillEffect extends ToggleSkillEffect {
  readonly skillId = 'tether';
  readonly failLabel = 'FIZZLE';

  private readonly deps: TetherDeps;
  private readonly config: TetherSkillConfig;

  private _shadeId = '';
  private _shadeAlive = false;
  private _fizzled = false;
  private _spawnsCount = 0;
  private _resolvedDamage = 0;
  private _lastSpawnX = 0;
  private _lastSpawnY = 0;

  constructor(deps: TetherDeps, config: Partial<TetherSkillConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters (observable by scenarios and integration layer) ---

  get shadeAlive(): boolean { return this._shadeAlive; }
  get shadeId(): string { return this._shadeId; }
  get fizzled(): boolean { return this._fizzled; }
  get spawnsCount(): number { return this._spawnsCount; }
  get resolvedDamage(): number { return this._resolvedDamage; }
  get lastSpawnX(): number { return this._lastSpawnX; }
  get lastSpawnY(): number { return this._lastSpawnY; }

  // --- Integration layer hook ---

  /**
   * Called by the integration layer when the shade is destroyed externally
   * (e.g., player death). Clears shade state and deactivates without
   * starting cooldown — the player is dead, cooldown is irrelevant.
   */
  onShadeDestroyed(): void {
    this._shadeAlive = false;
    this._shadeId = '';
    this._isActive = false;
  }

  // --- ToggleSkillEffect interface ---

  canActivate(): boolean {
    return !this._isActive && this.cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Freshness fizzle check — consumes cooldown but does not spawn
    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      this.cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    const pos = this.deps.player.getPosition();
    const facing = this.deps.player.getFacingDirection();
    const baseDamage = this.deps.player.getBaseDamage();

    // Resolve shade damage multiplier: max-aggregated (Clarity raises 0.4 → 0.55)
    const mult = this.deps.resolver.getMaxEffect('tether', 'shadeDamageMultiplier')
      ?? this.config.shadeDamageMultiplier;
    this._resolvedDamage = baseDamage * mult;

    // Resolve mirror distance: Clarity provides cycling override, otherwise config
    const mirrorDist = this.deps.getMirrorDistanceTiles?.()
      ?? this.config.mirrorDistanceTiles;

    // Compute spawn position: opposite of facing direction × mirror distance
    const offset = mirrorDist * this.config.tileSize;
    const spawnX = pos.x + (-facing.x) * offset;
    const spawnY = pos.y + (-facing.y) * offset;
    this._lastSpawnX = spawnX;
    this._lastSpawnY = spawnY;

    const shadeConfig: ShadeSummonConfig = {
      id: 'tethered_shade',
      archetype: 'shade',
      baseHp: 9999,  // shade has no independent HP; damage routes to player
      baseDamage: this._resolvedDamage,
      baseSpeed: this.config.shadeSpeed,
      leashRadiusTiles: 0,  // shade mirrors player position, no leash
      aggroWeight: this.config.shadeAggroWeight,
      aiMode: 'mirror',
      shadeDamageMultiplier: mult,
      mirrorDistanceTiles: mirrorDist,
      sharedHpPool: true,
    };

    this._shadeId = this.deps.summons.spawn(shadeConfig, spawnX, spawnY);
    this._shadeAlive = true;
    this._isActive = true;
    this._spawnsCount++;

    return true;
  }

  protected onDeactivate(): void {
    if (this._shadeAlive) {
      this.deps.summons.destroy(this._shadeId);
      this._shadeAlive = false;
      this._shadeId = '';
    }
    this.cooldownRemaining = this.config.cooldownMs;
  }

  protected updateWhileActive(_delta: number): void {
    // Mirror movement is handled by the integration layer. The shade's position
    // is computed each frame by the 'mirror' AI mode based on the player's
    // position and facing direction. The SkillEffect is the lifecycle
    // coordinator only — it does not drive per-frame position updates.
  }

  destroy(): void {
    if (this._shadeAlive) {
      this.deps.summons.destroy(this._shadeId);
      this._shadeAlive = false;
      this._shadeId = '';
    }
    super.destroy();
  }
}
